import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type {
  MyBookingsResponse,
  BookingResponse,
  PreviewRequest,
  PreviewResponse,
  CreateBookingRequest,
  ConfirmPaymentRequest,
} from '@/api/types';

export function useMyBookings(params?: {
  status?: string;
  upcoming?: boolean;
  page?: number;
  size?: number;
}) {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.upcoming !== undefined) searchParams.set('upcoming', String(params.upcoming));
  if (params?.page !== undefined) searchParams.set('page', String(params.page));
  if (params?.size !== undefined) searchParams.set('size', String(params.size));

  const query = searchParams.toString();
  const endpoint = `/users/me/bookings${query ? `?${query}` : ''}`;

  return useQuery({
    queryKey: ['myBookings', params],
    queryFn: () => apiClient<MyBookingsResponse>(endpoint),
  });
}

export function useBooking(id: string) {
  return useQuery({
    queryKey: ['booking', id],
    queryFn: () => apiClient<BookingResponse>(`/bookings/${id}`),
    enabled: !!id,
  });
}

export function useCancelBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, refundMethod }: { id: string; refundMethod?: string }) =>
      apiClient<BookingResponse>(`/bookings/${id}/cancel`, {
        method: 'PUT',
        body: JSON.stringify({ refundMethod }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myBookings'] });
      queryClient.invalidateQueries({ queryKey: ['booking'] });
      queryClient.invalidateQueries({ queryKey: ['creditBalance'] });
    },
  });
}

export function useExtendBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      additionalMinutes,
      paymentMethodId,
    }: {
      id: string;
      additionalMinutes: number;
      paymentMethodId?: string;
    }) =>
      apiClient<BookingResponse>(`/bookings/${id}/extend`, {
        method: 'PUT',
        body: JSON.stringify({ additionalMinutes, paymentMethodId }),
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['booking', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['myBookings'] });
      queryClient.invalidateQueries({ queryKey: ['creditBalance'] });
    },
  });
}

export function usePreviewBooking() {
  return useMutation({
    mutationFn: (request: PreviewRequest) =>
      apiClient<PreviewResponse>('/bookings/preview', {
        method: 'POST',
        body: JSON.stringify(request),
      }),
  });
}

export function useCreateBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      request,
      idempotencyKey,
    }: {
      request: CreateBookingRequest;
      idempotencyKey: string;
    }) =>
      apiClient<BookingResponse>('/bookings', {
        method: 'POST',
        body: JSON.stringify(request),
        idempotencyKey,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myBookings'] });
      queryClient.invalidateQueries({ queryKey: ['creditBalance'] });
    },
  });
}

export function useConfirmPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      bookingId,
      request,
    }: {
      bookingId: string;
      request: ConfirmPaymentRequest;
    }) =>
      apiClient<BookingResponse>(`/bookings/${bookingId}/confirm-payment`, {
        method: 'POST',
        body: JSON.stringify(request),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myBookings'] });
      queryClient.invalidateQueries({ queryKey: ['booking'] });
    },
  });
}
